import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { RemoveCircle, PlaylistAddCheck } from '@material-ui/icons';
import { parseISO, format } from 'date-fns';

import { UnidataTable, Modal } from '~/components';
import { t, canURL, number } from '~/helpers';
import { handleExportGeneric } from '~/helpers/exportReports';
import api from '~/services/api';
import { recordFiltering } from '~/store/modules/LogsDescarregamentos/actions';

import AssociarComponente from './AssociarComponente';
import DesconsiderarComponente from './DesconsiderarComponente';

export default function LogsDescarregamentos() {
  const data = useSelector((state) => state.logsdescarregamentos);
  const redux = useSelector((state) => state.logsdescarregamentos);
  const dispatch = useDispatch();

  const modalRefDes = useRef(null);
  const modalRefAss = useRef(null);

  const [loadingDes, setLoadingDes] = useState(false);
  const [hasPermissionDelete, setHasPermissionDelete] = useState(false);
  const [hasPermissionUpdate, setHasPermissionUpdate] = useState(false);

  const [selectedRow, setSelectedRow] = useState({});

  useEffect(() => {
    const url = canURL();

    if (url.update) {
      setHasPermissionUpdate(true);
    }

    if (url.delete) {
      setHasPermissionDelete(true);
    }
  }, []);

  const header = useMemo(
    () => [
      {
        id: 'datahoralogdescarga',
        label: t('s.operacao.recebimento.datahoradescarganf'),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
      },
      {
        id: 'nomeposto',
        label: t('s.operacao.recebimento.nomeposto'),
        filter: { type: 'text' },
      },
      {
        id: 'idpreset',
        label: t('s.cadastro.preset.idpreset'),
        filter: { type: 'text' },
      },
      {
        id: 'veiculologdescarga',
        label: t('s.menu.cadastro.veiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'volumedeclaradologdescarga',
        label: t('s.operacao.recebimento.compartimentos.volumedeclarado'),
        filter: { type: 'number' },
        formatted: (value) => number(value, 1),
      },
      {
        // id: 'idproduto',
        // label: t('s.cadastro.presetreceita.nomepresetreceita'),
        id: 'nomeabreviadoproduto',
        label: t('s.operacao.recebimento.nomeproduto'),
        filter: { type: 'text' },
      },
      {
        id: 'volumelogdescarga',
        label: t('s.operacao.logdescarga.volumedescarregado'),
        filter: { type: 'number' },
        formatted: (value) => number(value, 1),
      },
      {
        id: 'modooperacaologdescarga',
        label: t('s.operacao.recebimento.modorecebimento'),
        filter: { type: 'text' },
        formatted: (value) =>
          value === 'L'
            ? t('s.configuracao.tanquefisico.manual')
            : t('s.configuracao.bico.automaticobico'),
      },
      {
        id: 'erroprocessamentologdescarga',
        label: t('s.geral.camposistema.motivoiniciomanutencaodvec'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  const buttons = useMemo(() => {
    if (hasPermissionUpdate && hasPermissionDelete) {
      return [
        {
          title: t('s.operacao.logdescarregamentonaoprocessado.associar'), // 'Associar',
          action: (value) =>
            modalRefAss.current.handleOpenModal({
              data: value,
              dataLogsTable: data.params,
            }),
          icon: <PlaylistAddCheck />,
        },
        {
          title: t('s.operacao.logdescarregamentonaoprocessado.desconsiderar'), // 'Desconsiderar',
          action: () => modalRefDes.current.handleOpenModal(),
          icon: <RemoveCircle />,
        },
      ];
    }
    if (hasPermissionUpdate) {
      return [
        {
          title: t('s.operacao.logdescarregamentonaoprocessado.associar'), // 'Associar',
          action: (value) =>
            modalRefAss.current.handleOpenModal({
              data: value,
              dataLogsTable: data.params,
            }),
          icon: <PlaylistAddCheck />,
        },
      ];
    }
    if (hasPermissionDelete) {
      return [
        {
          title: t('s.operacao.logdescarregamentonaoprocessado.desconsiderar'), // 'Desconsiderar',
          action: () => modalRefDes.current.handleOpenModal(),
          icon: <RemoveCircle />,
        },
      ];
    }
    return [];
  }, [
    modalRefDes,
    modalRefAss,
    hasPermissionDelete,
    hasPermissionUpdate,
    data.params,
  ]);

  const handleDesconsiderarLog = useCallback(
    async ({ idlogdescarga }) => {
      setLoadingDes(true);
      try {
        await api.delete(`descarregamentosnaoprocessados/${idlogdescarga}`);
        toast.success(t('s.geral.sucessoexclusao'));
      } catch (err) {
        err.map((error) => toast.error(error));
      } finally {
        setLoadingDes(false);
        modalRefDes.current.handleCloseModal();
        dispatch(recordFiltering(data.params));
      }
    },
    [data.params, modalRefDes]
  );

  const ActionsModalDesconsiderar = useMemo(
    () => [
      {
        label: t('s.geral.botaoconfirmar'),
        callback: () => handleDesconsiderarLog(selectedRow),
        color: 'primary',
        autoFocus: true,
      },
      {
        label: t('s.geral.botaofechar'),
        callback: () => modalRefDes.current.handleCloseModal(),
        color: 'secondary',
      },
    ],
    [selectedRow]
  );

  const handleCloseModalAssociacao = useCallback(
    (params) => {
      modalRefAss.current.handleCloseModal();
      dispatch(recordFiltering(params));
    },
    [modalRefAss]
  );

  const ButtonsAss = useMemo(
    () => [
      {
        label: t('s.geral.botaofechar'),
        callback: () => handleCloseModalAssociacao(data.params),
        color: 'secondary',
      },
    ],
    [data.params]
  );

  const fieldlist = [
    'datahoralogdescarga',
    'nomeposto',
    'idpreset',
    'veiculologdescarga',
    'volumedeclaradologdescarga',
    'nomeabreviadoproduto',
    'volumelogdescarga',
    'modooperacaologdescarga',
    'erroprocessamentologdescarga',
  ];

  const substituirHeader = {
    nomeposto: t('s.operacao.recebimento.nomeposto'),
    idpreset: t('s.cadastro.preset.idpreset'),
    veiculologdescarga: t('s.menu.cadastro.veiculo'),
    volumedeclaradologdescarga: t(
      's.operacao.recebimento.compartimentos.volumedeclarado'
    ),
    nomeabreviadoproduto: t('s.operacao.recebimento.nomeproduto'),
    volumelogdescarga: t('s.operacao.logdescarga.volumedescarregado'),
    modooperacaologdescarga: t('s.operacao.recebimento.modorecebimento'),
    erroprocessamentologdescarga: t(
      's.geral.camposistema.motivoiniciomanutencaodvec'
    ),
    datahoralogdescarga: t('s.operacao.recebimento.datahoradescarganf'),
  };

  return (
    <>
      <UnidataTable
        fixed
        read={recordFiltering}
        create={false}
        delete={false}
        update={false}
        redux={data}
        header={header}
        actions={buttons}
        detach="idlogdescarga"
        keys="idlogdescarga"
        onRow={(value) => setSelectedRow(value)}
        pdf
        excel
        onExport={(formato) =>
          handleExportGeneric({
            endpoint: 'descarregamentosnaoprocessados',
            formato,
            fields: fieldlist,
            headers: substituirHeader,
            redux,
            ordination: redux.params.ordination,
            orderfield: redux.params.orderfield,
            title: 'Relatório de Descarregamentos Não Processados',
            substituirValores: {
              modooperacaologdescarga: {
                L: t('s.configuracao.tanquefisico.manual'),
                R: t('s.configuracao.bico.automaticobico'),
              },
            },
          })
        }
      />

      <Modal
        disableBackdropClick
        disableEscapeKeyDown
        ref={modalRefDes}
        maxWidth="xs"
        title={t('s.operacao.logdescarregamentonaoprocessado.desconsiderar')} // "Desconsiderar"
        component={DesconsiderarComponente}
        loading={loadingDes}
        actions={ActionsModalDesconsiderar}
      />

      <Modal
        ref={modalRefAss}
        title={t('s.operacao.logdescarregamentonaoprocessado.associarlog')} // "Associar Log"
        component={AssociarComponente}
        loading={loadingDes}
        actions={ButtonsAss}
      />
    </>
  );
}
